/**
 * Custom module for quilljs to allow specifying formatting allowed in paste operations
 * @see https://quilljs.com/blog/building-a-custom-module/
 */
export class PasteCleaner {
	constructor(quill, options = {}) {
		// save the quill reference
		this.options = defaultsDeep(options, PasteCleaner.getDefaultOptions());
		quill.clipboard.addMatcher('*', this.cleanDelta.bind(this));
	}

	// For default registered attributors and formats see
	// https://github.com/quilljs/quill/blob/189563a27fe18ef97688d948d458eabe316db1d3/quill.js#L42-L101
	static getDefaultOptions() {
		return {
			insert: {
				// <img>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/image.js#L53
				// Quill allows: 'alt', 'width', 'height'
				image: true,
			},
			attributes: {
				// CSS text-align
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/align.js#L5
				// Quill allows: ['right', 'center', 'justify']
				align: true,

				// image alt text
				alt: true,

				// CSS background-color
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/background.js#L7
				// Quill allows: (any value)
				background: false,

				// <blockquote>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/blockquote.js#L5
				// Quill allows [true, false]
				blockquote: false,

				// <b>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/bold.js#L20
				// Quill allows: [true, false]
				bold: true,

				// <br>
				// see https://github.com/quilljs/quill/blob/189563a27fe18ef97688d948d458eabe316db1d3/blots/break.js#L25
				// Quill allows: [true, false]
				break: true,

				// <code>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/code.js#L9
				// Quill allows: [true, false]
				code: false,

				// <pre>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/code.js#L113
				// Quill allows: [true, false]
				'code-block': false,

				// CSS color
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/color.js#L17
				// Quill allows: (any valid color value)
				color: false,

				// ?
				// see https://github.com/quilljs/quill/blob/e824bd59ec120fd91349975b2c3d75b2a3c5ea3c/blots/cursor.js#L111
				// Quill allows: [true, false]
				cursor: true,

				// "dir" attribute
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/direction.js#L8
				// Quill allows: ['ltr','rtl']
				direction: true,

				// CSS font-family
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/font.js#L16
				// Quill allows: ['serif', 'monospace']
				font: false,

				// katex formula
				// see https://github.com/quilljs/quill/blob/e824bd59ec120fd91349975b2c3d75b2a3c5ea3c/modules/formula.js#L20
				// Quill allows: (KaTeX if window.katex is set)
				formula: true,

				// <h1> through <h6>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/header.js
				// Quill allows: [1,2,3,4,5,6]
				header: false,

				// text indent level
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/indent.js#L26
				// Quill allows: [0, 1, 2, 3, 4, 5, 6, 7, 8]
				indent: false,

				// <i>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/italic.js#L4
				// Quill allows: [true, false]
				italic: true,

				// <a>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/link.js#L27
				// Quill allows: (<a> with certain attributes and protocols)
				link: true,

				// <ul> or <ol>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/list.js#L120
				// Quill allows: ['ordered', 'bullet']
				list: true,

				// <li>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/list.js#L123
				// Quill allows: ['LI']
				'list-item': true,

				// <sub> or <sup>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/script.js#L20
				// Quill allows: ['SUB', 'SUP']
				script: true,

				// ?
				// see https://github.com/quilljs/quill/blob/e824bd59ec120fd91349975b2c3d75b2a3c5ea3c/blots/scroll.js#L151
				// Quill allows: [true, false]
				scroll: false,

				// CSS font-size
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/size.js#L7
				// Quill allows: ['10px', '18px', '32px']
				size: false,

				// <s>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/strike.js#L4
				// Quill allows [true, false]
				strike: true,

				// <u>
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/underline.js#L4
				// Quill allows [true, false]
				underline: false,

				// video iframe
				// see https://github.com/quilljs/quill/blob/946e53ac4ed1d61abbe0fe606c3613c605ef93c9/formats/video.js
				// Quill allows: (certain values to construct an iframe)
				video: true,
			},
		};
	}

	cleanDelta(node, delta) {
		delta.ops.forEach(op => {
			this.processInsert(op);
			this.processAttributes(op);
		});
		return delta;
	}

	processInsert(op) {
		if (typeof op.insert != 'object') {
			// not an insert
			return;
		}
		// disallow insert if so specified
		forOwn(op.insert, (value, name) => {
			if (name in this.options.insert && !this.doesValueMatchSetting(this.options.insert[name], value)) {
				op.insert = '';
				op.attributes = {};
			}
		});
	}

	processAttributes(op) {
		forOwn(op.attributes, (value, name) => {
			if (name in this.options.attributes && !this.doesValueMatchSetting(this.options.attributes[name], value)) {
				// value is not allowed
				op.attributes[name] = false;
			}
		});
	}

	doesValueMatchSetting(setting, value) {
		if (!setting) {
			// not allowed at all
			return false;
		} else if (setting === true) {
			// any value allowed
			return true;
		} else if (setting instanceof RegExp) {
			// value matches regex
			return setting.test(value);
		} else if (typeof setting == 'object' && typeof setting.indexOf == 'function') {
			// value is one of the allowed items
			return setting.indexOf(value) > -1;
		} else if (typeof setting == 'string' || typeof setting == 'number') {
			// value is the given string or number
			return setting === value;
		} else if (typeof setting == 'function') {
			// value causes handler to return truthy
			return setting(value);
		}
		// another type such as an object
		return false;
	}
}

function forOwn(object, iterator) {
	if (!object) {
		return;
	}
	for (let prop in object) {
		if (object.hasOwnProperty(prop)) {
			iterator(object[prop], prop);
		}
	}
}

function defaultsDeep(obj, defaults) {
	forOwn(defaults, (value, prop) => {
		if (typeof value == 'object' && typeof obj[prop] == 'object') {
			defaultsDeep(obj[prop], value);
		} else if (!(prop in obj)) {
			obj[prop] = value;
		}
	});
	return obj;
}
